import { Icon } from 'components/common/Icon'
import { Separator } from 'components/common/Separator'
import { AdditionalInfo } from 'components/common/ways-to/AdditionalInfo'
import { WaysToBuy } from 'components/common/ways-to/WaysToBuy'
import { WaysToSell } from 'components/common/ways-to/WaysToSell'
import { useDrawer } from 'contexts'
import Trans from 'next-translate/Trans'
import useTranslation from 'next-translate/useTranslation'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useCallback, useEffect } from 'react'
import { NamedEntity } from 'types'
import { Edition } from 'utils/data'

export type WaysToStep = {
  title: string
  text: string
  imgName: string
  info?: string
}

export interface WaysToProps {
  type: 'buy' | 'sell'
  edition: Edition
  artists: NamedEntity[]
  artwork: NamedEntity
}

export const useWaysTo = () => {
  const { isOpen, setIsOpen, setConfig } = useDrawer()
  const router = useRouter()

  const setWaysTo = useCallback(
    (props: WaysToProps) => {
      setIsOpen(true)
      setConfig({
        direction: 'right',
        hasSeparator: true,
        hasIcon: true,
        heading: <WaysToHeader {...props} />,
        body: <WaysToContent {...props} />,
      })
    },
    [setConfig, setIsOpen]
  )

  useEffect(() => {
    if (isOpen) {
      setIsOpen(false)
    }
  }, [router.asPath])

  return setWaysTo
}

export const WaysToHeader = ({ type }: WaysToProps) => {
  const { t } = useTranslation('common')
  const { setIsOpen } = useDrawer()

  return (
    <div className='flex items-start justify-between'>
      <div>
        <h3 className='font-body text-lg font-medium tracking-normal'>
          <Trans
            i18nKey={`common:there_are_2_ways_to_${type}`}
            components={{
              comp: <span className='text-purple-500' />,
            }}
          />
        </h3>
        <h4 className='font-body text-sm font-normal tracking-normal text-cool-600'>
          {t('learn_more_on')}{' '}
          <Link
            href={`/how-it-works/for-${type === 'buy' ? 'buyers' : 'sellers'}`}
            className='text-secondary-default'
            onClick={() => setIsOpen(false)}
            target='_blank'
          >
            {type === 'buy' ? t('buyers_page') : t('sellers_page')}
          </Link>
        </h4>
      </div>
      <button type='button' onClick={() => setIsOpen(false)}>
        <Icon name='close' size={24} className='text-cool-600' />
      </button>
    </div>
  )
}

export const WaysToContent = ({ type, ...props }: WaysToProps) => {
  const { t } = useTranslation('common')

  return (
    <div className='h-full overflow-y-auto bg-warm-50 px-6 py-5'>
      {type === 'buy' && <WaysToBuy {...props} />}
      {type === 'sell' && <WaysToSell {...props} />}

      <Separator className='my-6' color='cool-400' />

      <AdditionalInfo />
    </div>
  )
}
